import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, ChevronDown, ChevronUp, Loader2, Plus, ShieldCheck, Star, Trash2, Truck } from "lucide-react";
import { api } from "../../lib/api";
import { formatEUR, countryFlag } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Panel, SpinnerRow, EmptyBox } from "./AdminShared";

/*
 * R3 Transports admin page.
 * Two sub-tabs: Richieste trasporto | Trasportatori
 */
export default function AdminTransports() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("requests");
  return (
    <div className="space-y-6" data-testid="admin-transports-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {t("admin.transports_overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter">
          {t("admin.transports_title")}
        </h1>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["requests", "admin.transports_tab.requests"], ["carriers", "admin.transports_tab.carriers"]].map(([k, key]) => (
          <button
            key={k}
            data-testid={`admin-transports-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
              tab === k ? "text-blue-600 border-blue-600" : "text-slate-500 border-transparent hover:text-slate-900"
            }`}
          >
            {t(key)}
          </button>
        ))}
      </div>
      {tab === "requests" ? <RequestsSection /> : <CarriersSection />}
    </div>
  );
}

// ───────────────── Requests ─────────────────
function RequestsSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/transport-requests").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  if (items === null) return <SpinnerRow />;
  return (
    <>
      {items.length === 0 ? (
        <EmptyBox text={t("admin.transports_no_requests")} />
      ) : (
        <ul className="space-y-2" data-testid="admin-transport-requests-list">
          {items.map((r) => <TransportRequestCard key={r.id} req={r} onChange={load} />)}
        </ul>
      )}
    </>
  );
}

function TransportRequestCard({ req, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-2xl bg-white border border-slate-100 shadow-sm" data-testid={`transport-request-${req.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left">
        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <Truck className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm">
            {req.vehicle_brand} {req.vehicle_model}
            {req.pratica?.code && <span className="ml-2 text-slate-400 text-xs">· {req.pratica.code}</span>}
          </div>
          <div className="text-xs text-slate-500">
            {req.pickup_city} → {req.delivery_city} · {t(`admin.transport_status.${req.status}`)}
            {req.selected_quote_id && <span className="ml-1 text-emerald-600 font-semibold"> · {t("admin.transports_assigned")}</span>}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <QuotesEditor req={req} onChange={onChange} />}
    </li>
  );
}

function QuotesEditor({ req, onChange }) {
  const { t } = useTranslation();
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState("");
  const [price, setPrice] = useState("");
  const [pickup, setPickup] = useState("");
  const [eta, setEta] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/carriers").then((r) => setCarriers(r.data || [])); }, []);

  const addQuote = async (e) => {
    e.preventDefault();
    if (!carrier || !price) return;
    setSaving(true);
    try {
      await api.post(`/admin/transport-requests/${req.id}/quotes`, {
        carrier_id: carrier,
        price_eur: parseFloat(price),
        pickup_date: pickup || null,
        delivery_eta: eta || null,
        notes: notes || null,
      });
      setPrice(""); setPickup(""); setEta(""); setNotes("");
      toast.success(t("admin.transports_quote_added"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally { setSaving(false); }
  };

  const selectQ = async (qid) => {
    try {
      await api.post(`/admin/transport-requests/${req.id}/select-quote/${qid}`);
      toast.success(t("admin.transports_quote_selected"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    }
  };

  const removeQ = async (qid) => {
    await api.delete(`/admin/transport-requests/${req.id}/quotes/${qid}`);
    onChange();
  };

  return (
    <div className="border-t border-slate-100 p-5 space-y-4">
      {/* Request details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div><span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold block">{t("admin.transports_pickup")}</span> <div className="mt-0.5 font-medium">{req.pickup_city || "—"}</div></div>
        <div><span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold block">{t("admin.transports_delivery")}</span> <div className="mt-0.5 font-medium">{req.delivery_city || "—"}</div></div>
        <div><span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold block">{t("admin.transports_vehicles")}</span> <div className="mt-0.5 font-medium tabular-nums">{req.vehicle_count || 1}</div></div>
        <div><span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold block">{t("admin.transports_avail_date")}</span> <div className="mt-0.5 font-medium tabular-nums">{req.available_date?.slice(0, 10) || "—"}</div></div>
      </div>

      {/* Quotes list */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{t("admin.transports_quotes")}</div>
        {(req.quotes || []).length === 0 ? (
          <div className="text-xs text-slate-400 py-2">{t("admin.transports_no_quotes")}</div>
        ) : (
          <ul className="space-y-1" data-testid={`transport-quotes-${req.id}`}>
            {req.quotes.map((q) => {
              const isSelected = req.selected_quote_id === q.id;
              return (
                <li
                  key={q.id}
                  data-testid={`transport-quote-${q.id}`}
                  className={`flex items-center gap-3 rounded-xl border p-3 text-sm ${
                    isSelected ? "border-emerald-300 bg-emerald-50" : "border-slate-100"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-sm">{q.carrier?.name || "—"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {q.pickup_date ? new Date(q.pickup_date).toLocaleDateString("it-IT") : "—"}
                      {" → "}
                      {q.delivery_eta ? new Date(q.delivery_eta).toLocaleDateString("it-IT") : "—"}
                      {q.notes && <span className="ml-1">· {q.notes}</span>}
                    </div>
                  </div>
                  <div className="text-right font-heading font-bold tabular-nums">{formatEUR(q.price_eur)}</div>
                  {isSelected ? (
                    <span className="text-[10px] font-semibold uppercase text-emerald-700 bg-emerald-100 rounded px-2 py-0.5">{t("admin.transports_selected")}</span>
                  ) : (
                    <>
                      <Button size="sm" onClick={() => selectQ(q.id)} data-testid={`transport-quote-select-${q.id}`} className="rounded-full h-8 bg-blue-600 hover:bg-blue-700 text-xs">
                        {t("admin.transports_select")}
                      </Button>
                      <button type="button" onClick={() => removeQ(q.id)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add quote */}
      <form onSubmit={addQuote} className="rounded-xl bg-slate-50 border border-slate-100 p-3 grid grid-cols-1 md:grid-cols-[1fr_120px_140px_140px_1fr_auto] gap-2" data-testid={`transport-quote-form-${req.id}`}>
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger className="h-10" data-testid="transport-quote-carrier"><SelectValue placeholder={t("admin.transports_choose_carrier")} /></SelectTrigger>
          <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" placeholder="€" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="transport-quote-price" className="h-10" />
        <Input type="date" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="transport-quote-pickup" className="h-10" />
        <Input type="date" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="transport-quote-eta" className="h-10" />
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("admin.transports_quote_notes")} data-testid="transport-quote-notes" className="h-10" />
        <Button type="submit" disabled={saving || !carrier || !price} data-testid="transport-quote-submit" className="rounded-full h-10 bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

// ───────────────── Carriers ─────────────────
function CarriersSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/carriers").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  if (items === null) return <SpinnerRow />;

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="admin-carrier-new" className="rounded-full bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" /> {t("admin.carrier_new")}
        </Button>
      </div>
      {creating && <CarrierEditor carrier={{}} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      <ul className="space-y-2" data-testid="admin-carriers-list">
        {items.map((c) => <CarrierRow key={c.id} carrier={c} onChange={load} />)}
      </ul>
    </>
  );
}

function CarrierRow({ carrier, onChange }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  if (editing) return <CarrierEditor carrier={carrier} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChange(); }} />;
  return (
    <li className="rounded-2xl bg-white border border-slate-100 p-4 flex items-center gap-3" data-testid={`admin-carrier-row-${carrier.id}`}>
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500"><Building2 className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-sm">{carrier.name}</span>
          {carrier.verification === "verified" && (
            <span className="text-[9px] font-semibold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
              <ShieldCheck className="w-2.5 h-2.5" />{t("admin.verified")}
            </span>
          )}
          {carrier.rating && (
            <span className="text-[10px] font-semibold text-amber-600 inline-flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-current" />{carrier.rating.toFixed(1)}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {countryFlag(carrier.country)} {(carrier.countries_covered || []).join(", ")} · {carrier.avg_days}g · {formatEUR(carrier.avg_price_eur || 0)} avg
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`admin-carrier-edit-${carrier.id}`}>{t("admin.edit")}</Button>
    </li>
  );
}

function CarrierEditor({ carrier, onCancel, onSaved }) {
  const { t } = useTranslation();
  const isNew = !carrier.id;
  const [form, setForm] = useState({
    name: carrier.name || "",
    country: carrier.country || "IT",
    countries_covered: (carrier.countries_covered || []).join(", "),
    frequent_routes: (carrier.frequent_routes || []).join(", "),
    avg_price_eur: carrier.avg_price_eur ?? "",
    avg_days: carrier.avg_days ?? "",
    insurance: carrier.insurance ?? true,
    capacity_vehicles: carrier.capacity_vehicles ?? "",
    contact_name: carrier.contact_name || "",
    contact_phone: carrier.contact_phone || "",
    contact_email: carrier.contact_email || "",
    rating: carrier.rating ?? "",
    verification: carrier.verification || "pending",
    notes: carrier.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        countries_covered: form.countries_covered.split(",").map((s) => s.trim()).filter(Boolean),
        frequent_routes: form.frequent_routes.split(",").map((s) => s.trim()).filter(Boolean),
        avg_price_eur: form.avg_price_eur === "" ? null : parseFloat(form.avg_price_eur),
        avg_days: form.avg_days === "" ? null : parseInt(form.avg_days),
        capacity_vehicles: form.capacity_vehicles === "" ? null : parseInt(form.capacity_vehicles),
        rating: form.rating === "" ? null : parseFloat(form.rating),
      };
      if (isNew) await api.post("/admin/carriers", body);
      else await api.patch(`/admin/carriers/${carrier.id}`, body);
      toast.success(t("admin.saved"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-blue-200 p-5 space-y-3 shadow-sm" data-testid="admin-carrier-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.name")}</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="carrier-name" required />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.country")}</Label>
          <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
            <SelectTrigger data-testid="carrier-country"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="IT">🇮🇹 IT</SelectItem><SelectItem value="DE">🇩🇪 DE</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.countries_covered")}</Label>
          <Input value={form.countries_covered} onChange={(e) => setForm({ ...form, countries_covered: e.target.value })} placeholder="DE, IT, AT" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.frequent_routes")}</Label>
          <Input value={form.frequent_routes} onChange={(e) => setForm({ ...form, frequent_routes: e.target.value })} placeholder="Milano - München" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.avg_price")} (€)</Label>
          <Input type="number" value={form.avg_price_eur} onChange={(e) => setForm({ ...form, avg_price_eur: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.avg_days")}</Label>
          <Input type="number" value={form.avg_days} onChange={(e) => setForm({ ...form, avg_days: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.capacity")}</Label>
          <Input type="number" value={form.capacity_vehicles} onChange={(e) => setForm({ ...form, capacity_vehicles: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.rating")}</Label>
          <Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.contact_name")}</Label>
          <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.contact_phone")}</Label>
          <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.contact_email")}</Label>
          <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.verification")}</Label>
          <Select value={form.verification} onValueChange={(v) => setForm({ ...form, verification: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="verified">{t("admin.verified")}</SelectItem>
              <SelectItem value="pending">{t("admin.pending")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
        <Label className="text-sm">{t("admin.carrier.insurance")}</Label>
        <Switch checked={form.insurance} onCheckedChange={(v) => setForm({ ...form, insurance: v })} />
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-500">{t("admin.carrier.notes")}</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button type="submit" disabled={saving} data-testid="carrier-save" className="rounded-full bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          {t("admin.save")}
        </Button>
      </div>
    </form>
  );
}
